import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Monitor, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, ArrowLeft, Star, DollarSign
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const LaserSoftwareGuide = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/laser/software-guide`)
      .then(res => {
        setEntries(res.data || []);
        if (res.data?.length > 0) setExpanded({ [res.data[0].id]: true });
      })
      .catch(err => console.error('Failed to load software guide:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.LASER_SOFTWARE)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Monitor className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading software guide...</p>
        </div>
      </div>
    );
  }

  const isLightBurn = (id) => id === 'lightburn';

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="laser-software-guide" style={bgStyle(IMAGES.LASER_SOFTWARE)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <Link to="/laser" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Monitor className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">SOFTWARE GUIDE</div>
            <h1 className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Laser Software
            </h1>
            <p className="text-sm text-zinc-300 mt-1">
              LightBurn · LaserGRBL · xTool Creative Space · RDWorks
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 max-w-4xl">
        {entries.map(entry => {
          const isOpen = expanded[entry.id];
          const isPrimary = isLightBurn(entry.id);

          return (
            <Card key={entry.id}
                  className={`bg-zinc-900 border-zinc-800 ${isPrimary ? 'border-orange-500/40' : ''}`}>
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => toggle(entry.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isPrimary ? 'bg-orange-500/20' : 'bg-zinc-800'}`}>
                      <Monitor className={`w-5 h-5 ${isPrimary ? 'text-orange-400' : 'text-zinc-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-white text-lg">{entry.name}</CardTitle>
                        {isPrimary && (
                          <Badge className="bg-orange-600 text-white text-xs flex items-center gap-1">
                            <Star className="w-3 h-3" /> Recommended
                          </Badge>
                        )}
                      </div>
                      <p className="text-zinc-400 text-sm">{entry.tagline}</p>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                           : <ChevronDown className="w-5 h-5 text-zinc-400 flex-shrink-0" />}
                </div>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-6 pt-0">
                  {/* Meta row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 bg-zinc-800/60 rounded-lg">
                      <div className="text-xs text-zinc-500 mb-1">Platforms</div>
                      <div className="text-sm text-white">{entry.platforms?.join(', ')}</div>
                    </div>
                    <div className="p-3 bg-zinc-800/60 rounded-lg">
                      <div className="text-xs text-zinc-500 mb-1">Price</div>
                      <div className="text-sm text-white">{entry.price}</div>
                    </div>
                    <div className="p-3 bg-zinc-800/60 rounded-lg">
                      <div className="text-xs text-zinc-500 mb-1">Compatible Controllers</div>
                      <div className="text-sm text-white">{entry.compatible_controllers?.join(', ')}</div>
                    </div>
                  </div>

                  {/* Best for */}
                  <div>
                    <h4 className="text-white font-semibold mb-2 text-sm">Best For</h4>
                    <ul className="space-y-1">
                      {entry.best_for?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                          <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Key features */}
                  {entry.key_features?.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-2 text-sm">Key Features</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                        {entry.key_features.map((feat, i) => (
                          <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                            <span className="text-orange-400 flex-shrink-0">•</span>
                            {feat}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Workspace tips */}
                  {entry.workspace_tips && (
                    <div className="p-4 rounded-xl bg-orange-900/50 border border-orange-500/40">
                      <h4 className="text-orange-300 font-semibold text-sm mb-1">Workspace Tips</h4>
                      <p className="text-orange-200 text-sm">{entry.workspace_tips}</p>
                    </div>
                  )}

                  {/* Common beginner mistakes */}
                  {entry.common_beginner_mistakes?.length > 0 && (
                    <div className="p-4 rounded-xl bg-red-900/50 border border-red-500/40">
                      <h4 className="text-red-300 font-semibold text-sm mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Common Beginner Mistakes
                      </h4>
                      <ul className="space-y-1">
                        {entry.common_beginner_mistakes.map((m, i) => (
                          <li key={i} className="flex items-start gap-2 text-red-200 text-sm">
                            <span className="flex-shrink-0">•</span>
                            {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Documentation */}
                  {entry.documentation && (
                    <p className="text-zinc-500 text-xs">{entry.documentation}</p>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {entries.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            <Monitor className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No software guide entries found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LaserSoftwareGuide;
